import type { DrawnBeam, DrawnNote, DrawnRest } from './drawn';
import {
  ENGRAVING,
  glyphHorizontalBounds,
  glyphVerticalBounds,
  ledgerHalfWidth,
  type SmuflGlyph,
} from './smufl';

interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
const CLEARANCE = 0.3;
const X_CLEARANCE = 0.15;

function glyphBox(glyph: SmuflGlyph, x: number, y: number, scale: number): Box {
  const h = glyphHorizontalBounds(glyph),
    v = glyphVerticalBounds(glyph);
  return {
    left: x + h.left * scale,
    right: x + h.right * scale,
    top: y + v.top * scale,
    bottom: y + v.bottom * scale,
  };
}

/** Move multi-voice rests to the nearest clear half-space. The whole glyph
 * must clear every chord head, ledger, accidental, stem, flag and beam.
 * Dense ledger chords can require a slot beyond the ordinary staff band. */
export function positionRests(notes: DrawnNote[], rests: DrawnRest[], beams: DrawnBeam[]): void {
  const obstacles: Box[] = [];
  for (const n of notes) {
    const scale = n.grace ? ENGRAVING.graceScale : n.cue ? ENGRAVING.cueScale : 1;
    for (const head of n.heads ?? [{ ...n, dx: 0 }]) {
      const x = n.x + head.dx;
      obstacles.push(glyphBox(n.notehead, x, head.y, scale));
      if (head.accidental != null)
        obstacles.push(
          glyphBox(head.accidental, n.x + (head.accidentalDx ?? -scale), head.y, scale),
        );
      for (const y of head.ledger)
        obstacles.push({
          left: x - ledgerHalfWidth(n.notehead, scale),
          right: x + ledgerHalfWidth(n.notehead, scale),
          top: y - ENGRAVING.staffLineThickness / 2,
          bottom: y + ENGRAVING.staffLineThickness / 2,
        });
    }
    if (n.stem) {
      const s = n.stem;
      obstacles.push({
        left: s.x - ENGRAVING.stemThickness / 2,
        right: s.x + ENGRAVING.stemThickness / 2,
        top: Math.min(s.y1, s.y2),
        bottom: Math.max(s.y1, s.y2),
      });
      if (n.flag != null) {
        // Flags use text-anchor=start in the renderer. Their measured ink
        // reaches at most 1.224 spaces right of the stem (Bravura metadata).
        const v = glyphVerticalBounds(n.flag);
        obstacles.push({
          left: s.x - 0.01 * scale,
          right: s.x + 1.224 * scale,
          top: s.y2 + v.top * scale,
          bottom: s.y2 + v.bottom * scale,
        });
      }
    }
    for (const mark of [...(n.artics ?? []), ...(n.ornaments ?? [])])
      obstacles.push(glyphBox(mark.glyph, n.x, mark.y, scale));
  }
  const restBoxes = (r: DrawnRest, y: number) => {
    const scale = r.cue ? ENGRAVING.cueScale : 1;
    return [
      glyphBox(r.glyph, r.x, y, scale),
      ...(r.ornaments ?? []).map((o) => glyphBox(o.glyph, r.x, o.y + y - r.y, scale)),
    ];
  };
  for (const r of rests) {
    const home = r.y;
    const ink = restBoxes(r, home);
    const left = Math.min(...ink.map((b) => b.left)) - X_CLEARANCE;
    const right = Math.max(...ink.map((b) => b.right)) + X_CLEARANCE;
    const near = obstacles.filter((b) => b.right > left && b.left < right);
    for (const other of rests)
      if (other !== r)
        near.push(...restBoxes(other, other.y).filter((b) => b.right > left && b.left < right));
    for (const beam of beams) {
      const lo = Math.max(left, Math.min(beam.x1, beam.x2)),
        hi = Math.min(right, Math.max(beam.x1, beam.x2));
      if (hi < lo) continue;
      const at = (x: number) =>
        beam.y1 + ((x - beam.x1) / (beam.x2 - beam.x1 || 1)) * (beam.y2 - beam.y1);
      const half = (ENGRAVING.beamThickness * (beam.grace ? ENGRAVING.graceScale : 1)) / 2;
      near.push({
        left: lo,
        right: hi,
        top: Math.min(at(lo), at(hi)) - half,
        bottom: Math.max(at(lo), at(hi)) + half,
      });
    }
    const clear = (y: number) =>
      restBoxes(r, y).every((a) =>
        near.every(
          (b) =>
            a.right + X_CLEARANCE <= b.left ||
            b.right + X_CLEARANCE <= a.left ||
            a.bottom + CLEARANCE <= b.top + 1e-7 ||
            b.bottom + CLEARANCE <= a.top + 1e-7,
        ),
      );
    if (clear(home)) continue;
    // A finite search extending just beyond local ink guarantees an outside
    // slot without pinning a rest inside a chord at an arbitrary y limit.
    const top = Math.min(home, ...near.map((b) => b.top));
    const bottom = Math.max(home, ...near.map((b) => b.bottom));
    const height = Math.max(...ink.map((b) => b.bottom)) - Math.min(...ink.map((b) => b.top));
    const steps = Math.ceil((Math.max(home - top, bottom - home) + height + 1) * 2);
    const dirs = home <= 2 ? [-1, 1] : [1, -1];
    let chosen = home;
    search: for (let k = 1; k <= steps; k++) {
      for (const dir of dirs) {
        const y = home + dir * k * 0.5;
        if (clear(y)) {
          chosen = y;
          break search;
        }
      }
    }
    for (const ornament of r.ornaments ?? []) ornament.y += chosen - home;
    r.y = chosen;
  }
}
